import React, { useEffect } from "react";
import ReactDOM from "react-dom";
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  InfoWindow,
} from "@react-google-maps/api";
import OpenMapsProvider from "../modules/map/open-maps-provider";
import mapStyles from "./search/util/map-style";
const containerStyle = {
  width: "100%",
  height: "750px",
};

const { useState, useCallback } = React;

const maps: Record<string, CallableFunction> = {
  google: google,
  "map-box": google, //there is currently no implementation of this so it should fallback to google
  "open-maps-provider": openStreetMaps,
};

interface Props {
  lat: number;
  lng: number;
  latOffset: number;
  lngOffset: number;
  offsetBreakpoint: number;
  icon: string;
  width: number;
  height: number;
  mapKey: string;
  styleHeight: string;
  styleWidth: string;
  mapStyles: string;
  zoom: number;
  maxZoom: number;
  info: string;
  hidePopups: boolean;
}

const App: React.FunctionComponent<Props> = (props) => {
  const [map, setMap] = useState(null);
  const [info, setInfo] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth)

  useEffect(() => {
    setWindowWidth(window.innerWidth)
    const abortController = new AbortController()
    window.addEventListener('resize', () => {
        setWindowWidth(window.innerWidth)
    }, {signal: abortController.signal} as any)

    return () => {
        abortController.abort()
    }
  }, [])

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: props.mapKey,
  });

  const toggleInfo = () => setInfo(!info);

  const onLoad = useCallback((map: any) => {
    setMap(map);
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  const options: any = {
    maxZoom: props.maxZoom || 11,
  };
  if (props.mapStyles) {
    options["styles"] = JSON.parse(props.mapStyles);
  }

  if (!isLoaded) return <div>Loading...</div>;

  const latOffset = windowWidth > (props.offsetBreakpoint ?? 0) ? props.latOffset : 0
  const lngOffset = windowWidth > (props.offsetBreakpoint ?? 0) ? props.lngOffset : 0

  return (
    <GoogleMap
      mapContainerStyle={{
        width: props.styleWidth || containerStyle.width,
        height: props.styleHeight || containerStyle.height,
      }}
      center={{
        lat: props.lat + latOffset,
        lng: props.lng + lngOffset,
      }}
      zoom={props.zoom || 15}
      onLoad={onLoad}
      onUnmount={onUnmount}
      options={options}
    >
      <Marker
        icon={{
          url: props.icon,
          size: new (window as any).google.maps.Size(props.width, props.height),
          origin: new (window as any).google.maps.Point(0, 0),
          anchor: new (window as any).google.maps.Point(
            props.width / 2,
            props.height / 2,
          ),
          scaledSize: new (window as any).google.maps.Size(
            props.width,
            props.height,
          ),
        }}
        position={{
          lat: props.lat,
          lng: props.lng,
        }}
        onClick={toggleInfo}
      >
        {props.hidePopups != true && info && props.info && (
          <InfoWindow onCloseClick={toggleInfo}>
            <>
              <div dangerouslySetInnerHTML={{ __html: props.info }}></div>
            </>
          </InfoWindow>
        )}
      </Marker>
    </GoogleMap>
  );
};

function openStreetMaps(element: HTMLElement) {
  const options: {
    mapOptions?: L.MapOptions;
    [key: string]: any;
  } = {
    openStreetMapsUrl: element.dataset["openStreetMapsUrl"],
    marker: {
      url: element.dataset["icon"],
      width: element.dataset["width"],
      height: element.dataset["height"],
    },
    cluster: element.dataset["cluster"] ?? false,
    zoom: element.dataset["zoom"],
    mapOptions: {
      scrollWheelZoom: element.dataset["scrollWheelZoom"] !== "false",
      doubleClickZoom: element.dataset["doubleClickZoom"] !== "false",
      zoomControl: element.dataset["zoomControl"] !== "false",
      dragging: element.dataset["dragging"] !== "false",
      keyboard: element.dataset["keyboard"] !== "false",
    },
    clusterOptions: {
      url: element.dataset["clusterMarkerUrl"],
      textColor: element.dataset["clusterTextColor"],
      textSize: element.dataset["clusterTextSize"],
      height: element.dataset["clusterHeight"],
      width: element.dataset["clusterWidth"],
      backgroundPosition: element.dataset["clusterBackgroundPosition"],
    }
  };
  const map = OpenMapsProvider.createMap(element, options);
  OpenMapsProvider.placePropertyPins(
    map,
    options,
    [
        ...JSON.parse(atob(element.dataset["allProperties"])),
      ...JSON.parse(atob(element.dataset["allExperiences"])),
    ],
    (property) => {
      return OpenMapsProvider.addMarker(map, options, property, {
        lat: property.latitude,
        lng: property.longitude,
      });
    },
  );
  OpenMapsProvider.placePropertyPins(
    map,
    options,
    [
      {
        latitude: element.dataset["latitude"],
        longitude: element.dataset["longitude"],
        type: 'current',
        description: atob(element.dataset["description"]),
      },
    ],
    (property) => {
      return OpenMapsProvider.addMarker(map, options, property, {
        lat: property.latitude,
        lng: property.longitude,
      });
    },
  );
}

function google(element: HTMLElement) {
  const info = element.innerHTML || "";
  ReactDOM.render(
    <App
      lat={parseFloat(element.dataset["latitude"])}
      lng={parseFloat(element.dataset["longitude"])}
      latOffset={parseFloat(element.dataset["latOffset"] ?? "0")}
      lngOffset={parseFloat(element.dataset["lngOffset"] ?? "0")}
      offsetBreakpoint={parseInt(element.dataset["offsetBreakpoint"])}
      icon={element.dataset["icon"]}
      width={parseInt(element.dataset["width"])}
      height={parseInt(element.dataset["height"])}
      mapKey={element.dataset["key"]}
      styleWidth={element.dataset["styleWidth"]}
      styleHeight={element.dataset["styleHeight"]}
      mapStyles={element.dataset["mapStyles"]}
      zoom={+element.dataset["zoom"]}
      maxZoom={+element.dataset["maxZoom"]}
      info={info}
      hidePopups={element.dataset["hidepopup"] === "true"}
    />,
    element,
  );
}

const setup = (): void => {
  const elements = [...document.querySelectorAll("[data-map]")];

  elements.forEach((element: HTMLElement) => {
    const type = (element.dataset["mapType"] ?? "google") as string;
    const map = maps[type];
    map(element);
  });
};

export default {
  setup,
};
